import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { CSVLink } from "react-csv";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import TableLoader from "../../../shared/TableLoader";
import NoDataFound from "../../../shared/NoDataFound";
import { BrandsTypes, toLakhs } from "../../../utils/constants";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ComparisonReport = () => {
  const now = new Date();
  const [startMonth, setStartMonth] = useState(new Date(now.getFullYear(), now.getMonth() - 3, 1));
  const [endMonth, setEndMonth] = useState(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const [brandType, setBrandType] = useState(2);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const csvLinkRef = useRef(null);
  const [csvData, setCsvData] = useState([]);

  // Build array of { year, month (0-indexed), key "YYYY-MM", label "Mon-YY" }
  const months = useMemo(() => {
    if (!startMonth || !endMonth || startMonth > endMonth) return [];
    const result = [];
    const cur = new Date(startMonth.getFullYear(), startMonth.getMonth(), 1);
    const end = new Date(endMonth.getFullYear(), endMonth.getMonth(), 1);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      result.push({
        key: `${y}-${String(m + 1).padStart(2, "0")}`,
        label: `${MONTH_ABBR[m]}-${String(y).slice(2)}`,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return result;
  }, [startMonth, endMonth]);

  const totalColspan = 4 + months.length * 4 + 4 + 6 + 1; // fixed(4) + monthly + total(4) + stats(6) + status(1)

  useEffect(() => {
    if (!startMonth || !endMonth || startMonth > endMonth) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: "/review-reports/getComparisonReport",
          method: "POST",
          data: {
            startMonth: `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, "0")}`,
            endMonth: `${endMonth.getFullYear()}-${String(endMonth.getMonth() + 1).padStart(2, "0")}`,
            brand_type: brandType,
          },
        });
        if (result.status === "SUCCESS") {
          setReportData(result.data || []);
        } else {
          setReportData([]);
          Toast("error", result?.message || "Failed to fetch comparison report");
        }
      } catch {
        setReportData([]);
        Toast("error", "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [startMonth, endMonth, brandType]);

  const fmtInserts = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const n = Number(val);
    return Number.isNaN(n) ? val : Math.round(n);
  };

  const fmtRevenue = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    return toLakhs(val);
  };

  const getMonthVal = (row, key, field) => {
    const val = row?.monthly_data?.[key]?.[field];
    return field.includes("revenue") ? fmtRevenue(val) : fmtInserts(val);
  };

  const getTotalVal = (row, field) => {
    const val = row?.total?.[field];
    return field.includes("revenue") ? fmtRevenue(val) : fmtInserts(val);
  };

  const getStatVal = (row, field) => {
    const val = row?.stats?.[field];
    if (val === null || val === undefined || val === "") return "-";
    if (field.includes("rev_per_day")) return fmtRevenue(val);
    const n = Number(val);
    return Number.isNaN(n) ? val : Math.round(n);
  };

  const handlePrepareCSV = () => {
    if (!reportData.length) return;
    const rows = reportData.map((row, i) => {
      const obj = {
        Sno: i + 1,
        Category: row.category ?? "",
        "Sales Person": row.sales_person ?? "",
        "Client Name": row.client_name ?? "",
      };
      months.forEach(({ key, label }) => {
        obj[`${label} Eenadu Inserts`] = row?.monthly_data?.[key]?.eenadu_inserts ?? "-";
        obj[`${label} Eenadu Revenue`] = row?.monthly_data?.[key]?.eenadu_revenue ?? "-";
        obj[`${label} W2N Inserts`] = row?.monthly_data?.[key]?.w2n_inserts ?? "-";
        obj[`${label} W2N Revenue`] = row?.monthly_data?.[key]?.w2n_revenue ?? "-";
      });
      obj["Total Eenadu Inserts"] = row?.total?.eenadu_inserts ?? "-";
      obj["Total Eenadu Revenue"] = row?.total?.eenadu_revenue ?? "-";
      obj["Total W2N Inserts"] = row?.total?.w2n_inserts ?? "-";
      obj["Total W2N Revenue"] = row?.total?.w2n_revenue ?? "-";
      obj["Eenadu Active Months"] = row?.stats?.eenadu_active_months ?? "-";
      obj["Eenadu Avg Days"] = row?.stats?.eenadu_avg_days ?? "-";
      obj["Eenadu Avg Rev/Day"] = row?.stats?.eenadu_avg_rev_per_day ?? "-";
      obj["W2N Active Months"] = row?.stats?.w2n_active_months ?? "-";
      obj["W2N Avg Days"] = row?.stats?.w2n_avg_days ?? "-";
      obj["W2N Avg Rev/Day"] = row?.stats?.w2n_avg_rev_per_day ?? "-";
      obj["Client Status"] = row?.client_status ?? "-";
      return obj;
    });
    setCsvData(rows);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);

  const th = "border border-gray-300 px-2 py-1 text-center text-xs font-semibold whitespace-nowrap";
  const td = "border border-gray-200 px-2 py-1 text-center text-xs whitespace-nowrap";
  const tdL = "border border-gray-200 px-2 py-1 text-left text-xs whitespace-nowrap max-w-[140px] truncate";

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="text-sm font-semibold text-gray-700">Comparison Report</div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold whitespace-nowrap">Brand Type:</span>
            <Select
              className="min-w-[170px]"
              placeholder="Select Brand Type"
              value={BrandsTypes.find((o) => o.value === brandType) || null}
              onChange={(s) => setBrandType(s ? s.value : null)}
              options={BrandsTypes}
              isClearable
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold whitespace-nowrap">From:</span>
            <DatePicker
              selected={startMonth}
              onChange={(d) => setStartMonth(d)}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              selectsStart
              startDate={startMonth}
              endDate={endMonth}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold whitespace-nowrap">To:</span>
            <DatePicker
              selected={endMonth}
              onChange={(d) => setEndMonth(d)}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              selectsEnd
              startDate={startMonth}
              endDate={endMonth}
              minDate={startMonth}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
            />
          </div>
          <CSVLink data={csvData} filename="comparison_report.csv" className="hidden" ref={csvLinkRef} />
          {reportData.length > 0 && !isLoading && (
            <button
              onClick={handlePrepareCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              Download
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="border-collapse border border-gray-300 text-xs bg-white">
          <thead>
            {/* Header row 1 - group labels */}
            <tr className="bg-gray-100">
              <th className={th} rowSpan={3}>Sno</th>
              <th className={th} rowSpan={3}>Category</th>
              <th className={th} rowSpan={3}>Sales Person</th>
              <th className={th} rowSpan={3}>Client Name</th>
              {months.map(({ label }) => (
                <th key={label} className={`${th} bg-blue-50`} colSpan={4}>
                  {label}
                </th>
              ))}
              <th className={`${th} bg-emerald-50`} colSpan={4}>Total</th>
              <th className={`${th} bg-purple-50`} colSpan={6}>Stats</th>
              <th className={th} rowSpan={3}>Client Status</th>
            </tr>

            {/* Header row 2 - brand groups */}
            <tr className="bg-gray-50">
              {months.map(({ key }) => (
                <React.Fragment key={key}>
                  <th className={`${th} bg-blue-50`} colSpan={2}>Eenadu</th>
                  <th className={`${th} bg-sky-100`} colSpan={2}>Way2News</th>
                </React.Fragment>
              ))}
              <th className={`${th} bg-emerald-50`} colSpan={2}>Eenadu</th>
              <th className={`${th} bg-teal-100`} colSpan={2}>Way2News</th>
              <th className={`${th} bg-purple-50`} colSpan={3}>Eenadu</th>
              <th className={`${th} bg-violet-100`} colSpan={3}>Way2News</th>
            </tr>

            {/* Header row 3 - leaf columns */}
            <tr className="bg-gray-50">
              {months.map(({ key }) => (
                <React.Fragment key={key}>
                  <th className={`${th} bg-blue-50`}>Inserts</th>
                  <th className={`${th} bg-blue-50`}>Revenue</th>
                  <th className={`${th} bg-sky-100`}>Inserts</th>
                  <th className={`${th} bg-sky-100`}>Revenue</th>
                </React.Fragment>
              ))}
              <th className={`${th} bg-emerald-50`}>Inserts</th>
              <th className={`${th} bg-emerald-50`}>Revenue</th>
              <th className={`${th} bg-teal-100`}>Inserts</th>
              <th className={`${th} bg-teal-100`}>Revenue</th>
              <th className={`${th} bg-purple-50`}>Active Months</th>
              <th className={`${th} bg-purple-50`}>Avg Days</th>
              <th className={`${th} bg-purple-50`}>Avg Rev/Day</th>
              <th className={`${th} bg-violet-100`}>Active Months</th>
              <th className={`${th} bg-violet-100`}>Avg Days</th>
              <th className={`${th} bg-violet-100`}>Avg Rev/Day</th>
            </tr>
          </thead>

          {isLoading ? (
            <TableLoader colspan={totalColspan} />
          ) : !reportData.length ? (
            <NoDataFound colspan={totalColspan} />
          ) : (
            <tbody>
              {reportData.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className={td}>{i + 1}</td>
                  <td className={tdL} title={row.category}>{row.category}</td>
                  <td className={tdL} title={row.sales_person}>{row.sales_person}</td>
                  <td className={tdL} title={row.client_name}>{row.client_name}</td>
                  {months.map(({ key }) => (
                    <React.Fragment key={key}>
                      <td className={td} title={row?.monthly_data?.[key]?.eenadu_inserts}>
                        {getMonthVal(row, key, "eenadu_inserts")}
                      </td>
                      <td className={td} title={row?.monthly_data?.[key]?.eenadu_revenue}>
                        {getMonthVal(row, key, "eenadu_revenue")}
                      </td>
                      <td className={td} title={row?.monthly_data?.[key]?.w2n_inserts}>
                        {getMonthVal(row, key, "w2n_inserts")}
                      </td>
                      <td className={td} title={row?.monthly_data?.[key]?.w2n_revenue}>
                        {getMonthVal(row, key, "w2n_revenue")}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className={td} title={row?.total?.eenadu_inserts}>{getTotalVal(row, "eenadu_inserts")}</td>
                  <td className={td} title={row?.total?.eenadu_revenue}>{getTotalVal(row, "eenadu_revenue")}</td>
                  <td className={td} title={row?.total?.w2n_inserts}>{getTotalVal(row, "w2n_inserts")}</td>
                  <td className={td} title={row?.total?.w2n_revenue}>{getTotalVal(row, "w2n_revenue")}</td>
                  <td className={td}>{getStatVal(row, "eenadu_active_months")}</td>
                  <td className={td}>{getStatVal(row, "eenadu_avg_days")}</td>
                  <td className={td} title={row?.stats?.eenadu_avg_rev_per_day}>{getStatVal(row, "eenadu_avg_rev_per_day")}</td>
                  <td className={td}>{getStatVal(row, "w2n_active_months")}</td>
                  <td className={td}>{getStatVal(row, "w2n_avg_days")}</td>
                  <td className={td} title={row?.stats?.w2n_avg_rev_per_day}>{getStatVal(row, "w2n_avg_rev_per_day")}</td>
                  <td className={td}>{row?.client_status ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default ComparisonReport;
